import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Search, Plus, Folder, ChevronUp, Bot, Building2, Trash2, X, Pencil } from 'lucide-react';
import DocumentManagerModal from '@/components/purchasing/DocumentManagerModal';
import toast from 'react-hot-toast';
import SearchableCombobox from '@/components/ui/SearchableCombobox';

export default function PurchasingDocuments() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('abjad');
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newMaterialName, setNewMaterialName] = useState('');
  const [newVendorName, setNewVendorName] = useState('');
  const [editingDocId, setEditingDocId] = useState(null);
  
  const [selectedDocsList, setSelectedDocsList] = useState([]);
  const [isDeletingBulk, setIsDeletingBulk] = useState(false);

  // Fetch documents
  const { data: documents = [], isLoading } = useQuery({
    queryKey: ['purchasing-documents'],
    queryFn: () => base44.entities.PurchasingDocument.list('-created_date'),
  });

  // Derived state
  const filteredDocs = documents.filter(doc => {
    if (!search) return true;
    const q = search.toLowerCase();
    return doc.material_name?.toLowerCase().includes(q) || doc.vendor_name?.toLowerCase().includes(q);
  }).sort((a, b) => {
    if (sortBy === 'abjad') {
      return (a.material_name || '').localeCompare(b.material_name || '');
    } else if (sortBy === 'vendor') {
      return (a.vendor_name || '').localeCompare(b.vendor_name || '');
    }
    return 0;
  });

  const uniqueVendors = Array.from(new Set(documents.map(d => d.vendor_name).filter(v => v && v !== '-'))).sort();

  const groupedByVendor = React.useMemo(() => {
    if (sortBy !== 'vendor') return null;
    const groups = {};
    filteredDocs.forEach(doc => {
      const vendor = doc.vendor_name || '-';
      if (!groups[vendor]) groups[vendor] = [];
      groups[vendor].push(doc);
    });
    return groups;
  }, [filteredDocs, sortBy]);

  const handleSaveFolder = async () => {
    if (!newMaterialName.trim()) return;
    
    try {
      if (editingDocId) {
        await base44.entities.PurchasingDocument.update(editingDocId, {
          material_name: newMaterialName.trim(),
          vendor_name: newVendorName.trim() || "-",
        });
        toast.success('Folder dokumen berhasil diperbarui!');
      } else {
        await base44.entities.PurchasingDocument.create({
          material_name: newMaterialName.trim(),
          vendor_name: newVendorName.trim() || "-",
          msds_url: null,
          coa_url: null,
          kipra_url: null,
          halal_url: null
        });
        toast.success('Folder dokumen berhasil dibuat!');
      }
      
      queryClient.invalidateQueries({ queryKey: ['purchasing-documents'] });
      setShowAddDialog(false);
      setNewMaterialName('');
      setNewVendorName('');
      setEditingDocId(null);
    } catch (error) {
      console.error(error);
      toast.error('Gagal menyimpan folder!');
    }
  };

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const toggleSelectDoc = (e, id) => {
    e.stopPropagation();
    setSelectedDocsList(prev => 
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedDocsList.length === filteredDocs.length) {
      setSelectedDocsList([]);
    } else {
      setSelectedDocsList(filteredDocs.map(d => d.id));
    }
  };

  const handleDeleteSelected = async () => {
    setIsDeletingBulk(true);
    const toastId = toast.loading('Menghapus data...');
    try {
      await Promise.all(selectedDocsList.map(id => base44.entities.PurchasingDocument.hardDelete(id)));
      toast.success(`${selectedDocsList.length} data berhasil dihapus!`, { id: toastId });
      setSelectedDocsList([]);
      queryClient.invalidateQueries({ queryKey: ['purchasing-documents'] });
    } catch (e) {
      toast.error('Gagal menghapus beberapa data.', { id: toastId });
    } finally {
      setIsDeletingBulk(false);
    }
  };

  return (
    <div className="space-y-6 text-[#302621] relative pb-20">
      
      {/* FLOATING ACTION BAR FOR MULTIPLE SELECTION */}
      {selectedDocsList.length > 0 && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 bg-white border border-slate-200 shadow-2xl rounded-2xl p-2 px-4 flex items-center gap-4 animate-in slide-in-from-top-10">
          <label className="flex items-center gap-2 text-sm font-bold text-slate-700 cursor-pointer">
            <input 
              type="checkbox" 
              checked={selectedDocsList.length === filteredDocs.length && filteredDocs.length > 0}
              onChange={handleSelectAll}
              className="w-4 h-4 rounded border-slate-300 text-[#8C6208] focus:ring-[#8C6208]"
            />
            Pilih Semua
          </label>
          <div className="w-px h-5 bg-slate-200"></div>
          <span className="text-sm font-bold text-rose-600 bg-rose-50 px-3 py-1 rounded-lg">
            {selectedDocsList.length} Terpilih
          </span>
          <div className="w-px h-5 bg-slate-200"></div>
          <Button 
            onClick={handleDeleteSelected} 
            disabled={isDeletingBulk}
            className="h-8 gap-2 bg-rose-500 hover:bg-rose-600 text-white text-xs font-bold rounded-xl"
          >
            <Trash2 className="w-3.5 h-3.5" /> Hapus Data
          </Button>
          <button 
            onClick={() => setSelectedDocsList([])}
            className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors ml-2"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="flex flex-wrap justify-between items-center gap-4 border-b border-slate-100 pb-5">
        <div>
          <h1 className="text-2xl font-black text-[#302621] tracking-tight">Library Dokumen Bahan Baku</h1>
          <p className="text-xs text-[#7A695B] mt-0.5">Pusat penyimpanan MSDS, COA, KIPRA, dan Sertifikat HALAL.</p>
        </div>
        <Button onClick={() => {
          setEditingDocId(null);
          setNewMaterialName('');
          setNewVendorName('');
          setShowAddDialog(true);
        }} className="h-9 gap-1.5 text-xs bg-[#302621] hover:bg-slate-800 text-white font-bold rounded-xl shadow-sm">
          <Plus className="w-4 h-4" /> Tambah Bahan Baru
        </Button>
      </div>

      <div className="flex flex-wrap gap-2 items-center bg-white p-3 rounded-2xl border border-slate-100 shadow-sm">
        <div className="relative w-full sm:w-auto flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A39171]" />
          <Input 
            placeholder="Cari bahan baku atau pabrikan..." 
            className="pl-9 h-10 text-sm w-full bg-slate-50 border-transparent focus-visible:bg-white focus-visible:border-[#8C6208] focus-visible:ring-1 focus-visible:ring-[#8C6208]/20 transition-all rounded-xl"
            value={search} 
            onChange={(e) => setSearch(e.target.value)} 
          />
        </div>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="h-10 w-40 text-xs bg-slate-50 border-transparent rounded-xl focus:ring-0">
            <SelectValue placeholder="Urutkan" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="abjad" className="text-xs font-medium">Urut Abjad (A-Z)</SelectItem>
            <SelectItem value="vendor" className="text-xs font-medium">Urut by Pabrikan</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-[#7A695B] text-sm animate-pulse">Memuat data library...</div>
      ) : filteredDocs.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-3xl border border-slate-100 border-dashed">
          <Folder className="w-12 h-12 text-slate-200 mx-auto mb-3" />
          <p className="text-sm font-bold text-slate-400">Library masih kosong atau bahan tidak ditemukan.</p>
          <p className="text-xs text-slate-400 mt-1">Klik tombol "Tambah Bahan Baru" di atas untuk memulai.</p>
        </div>
      ) : sortBy === 'vendor' && groupedByVendor ? (
        <div className="space-y-8">
          {Object.entries(groupedByVendor).sort(([a], [b]) => a.localeCompare(b)).map(([vendor, docs]) => (
            <div key={vendor} className="space-y-4">
              <div className="flex items-center gap-3 bg-[#4A3B32] text-white p-4 rounded-xl shadow-sm">
                <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
                  <Building2 className="w-5 h-5 text-[#E6D5B8]" />
                </div>
                <div>
                  <h2 className="text-sm font-black uppercase tracking-wider">{vendor === '-' ? 'Tanpa Pabrikan' : vendor}</h2>
                  <p className="text-[11px] text-[#E6D5B8] font-medium">{docs.length} ITEM DOKUMEN</p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
                {docs.map(doc => {
                  const uploadedCount = [doc.msds_url, doc.coa_url, doc.kipra_url, doc.halal_url].filter(Boolean).length;
                  
                  return (
                    <div 
                      key={doc.id}
                      onClick={() => setSelectedDoc(doc)}
                      className={`group relative border p-5 rounded-3xl shadow-sm cursor-pointer transition-all duration-300 overflow-hidden
                        ${selectedDocsList.includes(doc.id) 
                          ? 'border-rose-500 bg-rose-50/40 shadow-rose-100' 
                          : 'bg-white border-slate-100 hover:border-[#E6D5B8] hover:shadow-md hover:-translate-y-1'
                        }`}
                    >
                      <div className="absolute -right-6 -top-6 w-24 h-24 bg-gradient-to-br from-[#F9F5EC] to-transparent rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                      
                      <div className="flex items-start gap-3 relative z-10 pr-12">
                        <div className="w-10 h-10 rounded-2xl bg-[#F9F5EC] text-[#8C6208] flex items-center justify-center shrink-0">
                          <Folder className="w-5 h-5 fill-[#E6D5B8]/30" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="font-bold text-sm text-[#302621] truncate group-hover:text-[#8C6208] transition-colors pr-2">{doc.material_name}</h3>
                          <p className="text-[11px] font-semibold text-[#A39171] uppercase tracking-wider truncate mt-0.5">{doc.vendor_name}</p>
                        </div>
                      </div>
                      
                      <div className="mt-5 pt-4 border-t border-slate-50 flex items-center justify-between">
                        <div className="flex -space-x-2">
                          {[
                            { key: 'msds_url', label: 'M' },
                            { key: 'coa_url', label: 'C' },
                            { key: 'kipra_url', label: 'K' },
                            { key: 'halal_url', label: 'H' }
                          ].map((type, i) => (
                            <div 
                              key={type.key}
                              className={`w-6 h-6 rounded-full border-2 border-white flex items-center justify-center text-[9px] font-black
                                ${doc[type.key] ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-300'}`}
                              title={doc[type.key] ? `Ada Dokumen` : `Belum Ada`}
                            >
                              {type.label}
                            </div>
                          ))}
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingDocId(doc.id);
                                setNewMaterialName(doc.material_name);
                                setNewVendorName(doc.vendor_name === '-' ? '' : doc.vendor_name);
                                setShowAddDialog(true);
                              }}
                              className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-[#8C6208] hover:bg-[#F9F5EC] rounded-lg transition-colors"
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleSelectDoc(e, doc.id);
                              }}
                              className={`w-7 h-7 flex items-center justify-center rounded-lg transition-colors ${
                                selectedDocsList.includes(doc.id)
                                  ? 'bg-rose-500 text-white hover:bg-rose-600'
                                  : 'text-rose-300 hover:text-rose-600 hover:bg-rose-50'
                              }`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          <div className="text-[10px] font-bold text-slate-400 bg-slate-50 px-2 py-1 rounded-lg">
                            {uploadedCount}/4
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredDocs.map(doc => {
            const uploadedCount = [doc.msds_url, doc.coa_url, doc.kipra_url, doc.halal_url].filter(Boolean).length;
            
            return (
              <div 
                key={doc.id}
                onClick={() => setSelectedDoc(doc)}
                className={`group relative border p-5 rounded-3xl shadow-sm cursor-pointer transition-all duration-300 overflow-hidden
                  ${selectedDocsList.includes(doc.id) 
                    ? 'border-rose-500 bg-rose-50/40 shadow-rose-100' 
                    : 'bg-white border-slate-100 hover:border-[#E6D5B8] hover:shadow-md hover:-translate-y-1'
                  }`}
              >
                <div className="absolute -right-6 -top-6 w-24 h-24 bg-gradient-to-br from-[#F9F5EC] to-transparent rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                <div className="flex items-start gap-3 relative z-10 pr-12">
                  <div className="w-10 h-10 rounded-2xl bg-[#F9F5EC] text-[#8C6208] flex items-center justify-center shrink-0">
                    <Folder className="w-5 h-5 fill-[#E6D5B8]/30" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-bold text-sm text-[#302621] truncate group-hover:text-[#8C6208] transition-colors pr-2">{doc.material_name}</h3>
                    <p className="text-[11px] font-semibold text-[#A39171] uppercase tracking-wider truncate mt-0.5">{doc.vendor_name}</p>
                  </div>
                </div>
                
                <div className="mt-5 pt-4 border-t border-slate-50 flex items-center justify-between">
                  <div className="flex -space-x-2">
                    {[
                      { key: 'msds_url', label: 'M' },
                      { key: 'coa_url', label: 'C' },
                      { key: 'kipra_url', label: 'K' },
                      { key: 'halal_url', label: 'H' }
                    ].map((type, i) => (
                      <div 
                        key={type.key}
                        className={`w-6 h-6 rounded-full border-2 border-white flex items-center justify-center text-[9px] font-black
                          ${doc[type.key] ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-300'}`}
                        title={doc[type.key] ? `Ada Dokumen` : `Belum Ada`}
                      >
                        {type.label}
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingDocId(doc.id);
                          setNewMaterialName(doc.material_name);
                          setNewVendorName(doc.vendor_name === '-' ? '' : doc.vendor_name);
                          setShowAddDialog(true);
                        }}
                        className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-[#8C6208] hover:bg-[#F9F5EC] rounded-lg transition-colors"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleSelectDoc(e, doc.id);
                        }}
                        className={`w-7 h-7 flex items-center justify-center rounded-lg transition-colors ${
                          selectedDocsList.includes(doc.id)
                            ? 'bg-rose-500 text-white hover:bg-rose-600'
                            : 'text-rose-300 hover:text-rose-600 hover:bg-rose-50'
                        }`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className="text-[10px] font-bold text-slate-400 bg-slate-50 px-2 py-1 rounded-lg">
                      {uploadedCount}/4
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {selectedDoc && (
        <DocumentManagerModal 
          open={!!selectedDoc}
          doc={selectedDoc}
          onClose={() => setSelectedDoc(null)}
          onSaved={() => queryClient.invalidateQueries({ queryKey: ['purchasing-documents'] })}
        />
      )}

      <button 
        onClick={scrollToTop}
        className="fixed bottom-[80px] md:bottom-8 right-6 md:right-8 z-50 w-12 h-12 bg-[#8C6208] hover:bg-[#6A4A06] text-white rounded-full shadow-xl flex items-center justify-center transition-transform hover:-translate-y-1 print:hidden"
      >
        <ChevronUp className="w-6 h-6" />
      </button>

      {/* Add Document Dialog (Asa Themed) */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md bg-[#FFFDF9] border-[#E6D5B8] p-0 overflow-hidden">
          <DialogHeader className="p-6 border-b border-[#E6D5B8]/40 bg-[#F9F5EC]">
            <DialogTitle className="flex items-center gap-3 text-xl font-black text-[#8C6208]">
              <Folder className="w-6 h-6" />
              {editingDocId ? 'Edit Bahan & Pabrikan' : 'Tambah Bahan Baru'}
            </DialogTitle>
          </DialogHeader>
          <div className="p-6">
            <div className="bg-[#FFFDF9] border border-[#E6D5B8] p-4 rounded-xl flex items-start gap-3 mb-6">
              <Bot className="w-6 h-6 text-[#8C6208] mt-0.5" />
              <p className="text-sm text-[#7A695B] leading-relaxed">
                Hai! Asa akan bantu {editingDocId ? 'perbarui' : 'buatkan'} foldernya. Lengkapi form di bawah ya! 🌟
              </p>
            </div>
            
            <div className="space-y-5">
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-[#302621]">Nama Bahan Baku <span className="text-rose-500">*</span></label>
                <Input 
                  placeholder="Contoh: Niacinamide 10%" 
                  className="bg-white border-[#E6D5B8] focus-visible:ring-[#8C6208]"
                  value={newMaterialName}
                  onChange={(e) => setNewMaterialName(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-[#302621]">Nama Pabrikan (Opsional)</label>
                <SearchableCombobox 
                  value={newVendorName}
                  onValueChange={setNewVendorName}
                  options={uniqueVendors}
                  placeholder="Pilih atau ketik pabrikan baru..."
                  searchPlaceholder="Cari pabrikan..."
                  allowCustom={true}
                  className="w-full"
                />
              </div>
            </div>
          </div>
          <DialogFooter className="p-4 border-t border-[#E6D5B8]/40 bg-slate-50 flex gap-2 sm:justify-end">
            <Button variant="ghost" onClick={() => setShowAddDialog(false)} className="text-[#7A695B] hover:bg-[#E6D5B8]/20">
              Batal
            </Button>
            <Button onClick={handleSaveFolder} disabled={!newMaterialName.trim()} className="bg-[#8C6208] hover:bg-[#6A4A06] text-white font-bold">
              {editingDocId ? 'Simpan Perubahan' : 'Buat Folder Dokumen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
